import { apiClient } from '@/lib/api/client';
import type { User } from '@/lib/api/types';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || '';

export interface ChatConversation {
  conversation_id: string;
  type: 'public' | 'internal';
  status: 'open' | 'closed';
  title: string;
  visitor_name?: string;
  visitor_phone?: string;
  service_request_id?: string;
  service_request_number?: string;
  assigned_user_ids: string[];
  assigned_user_names?: string[];
  participant_user_ids: string[];
  participant_names?: string[];
  last_message_at?: string;
  last_message_preview?: string;
  unread_count: number;
  created_at: string;
}

export interface ChatAttachment {
  attachment_id: string;
  conversation_id: string;
  message_id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export interface ChatMessage {
  message_id: string;
  conversation_id: string;
  author_kind: 'visitor' | 'staff' | 'system';
  author_user_id?: string;
  author_name: string;
  body: string;
  attachments?: ChatAttachment[];
  created_at: string;
}

/** Файлы в строке ввода (ещё не отправленные в сообщении) — синхронизируются в live-typing для оператора. */
export interface ChatComposerPendingDraft {
  attachment_id?: string;
  original_filename?: string;
  mime_type?: string;
  size_bytes?: number;
  uploading?: boolean;
}

export interface ChatTypingState {
  author_kind: 'visitor' | 'staff' | 'system';
  author_user_id?: string;
  author_name: string;
  body_preview?: string;
  pending_attachments?: ChatComposerPendingDraft[];
  is_typing: boolean;
}

export interface ChatEvent {
  type: string;
  conversation_id?: string;
  conversation?: ChatConversation;
  message?: ChatMessage;
  typing?: ChatTypingState;
}

/** Объединяет сообщения по id, порядок — по created_at. */
export function mergeChatMessagesById(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const map = new Map<string, ChatMessage>();
  for (const m of prev) map.set(m.message_id, m);
  for (const m of incoming) map.set(m.message_id, m);
  return Array.from(map.values()).sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

export interface PublicChatStartResponse {
  conversation: ChatConversation;
  visitor_token: string;
}

export type { VisitorContext, VisitorFelixCustomer, VisitorSubscriber } from './chat-visitor-context';
export { fetchVisitorContext } from './chat-visitor-context';

export const MAX_CHAT_ATTACHMENT_BYTES = 25 * 1024 * 1024;

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Client-ID': apiClient.clientId,
  };
  const token = apiClient.getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseResponse<T>(res: Response, fallback: string): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok || (json?.status?.code && json.status.code >= 400)) {
    throw new Error(json?.status?.message || fallback);
  }
  return json.data as T;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export async function listChatConversations(params: {
  scope?: string;
  status?: string;
  search?: string;
  limit?: number;
} = {}): Promise<{ conversations: ChatConversation[]; total: number }> {
  const sp = new URLSearchParams();
  if (params.scope) sp.set('scope', params.scope);
  if (params.status) sp.set('status', params.status);
  if (params.search) sp.set('search', params.search);
  sp.set('limit', String(params.limit ?? 80));
  const res = await fetch(`${API_BASE}/api/v1/chat/conversations?${sp.toString()}`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  const data = await parseResponse<{ conversations: ChatConversation[] | null; total: number }>(
    res,
    'Не удалось загрузить чаты'
  );
  return {
    conversations: asArray<ChatConversation>(data.conversations),
    total: typeof data.total === 'number' ? data.total : 0,
  };
}

export async function listChatMessages(
  conversationId: string,
  params: { limit?: number; beforeMessageId?: string } = {}
): Promise<{ messages: ChatMessage[]; has_more_older: boolean }> {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.beforeMessageId) sp.set('before_message_id', params.beforeMessageId);
  const qs = sp.toString();
  const url = qs
    ? `${API_BASE}/api/v1/chat/conversations/${conversationId}/messages?${qs}`
    : `${API_BASE}/api/v1/chat/conversations/${conversationId}/messages`;
  const res = await fetch(url, {
    headers: authHeaders(),
    credentials: 'include',
  });
  const data = await parseResponse<{ messages: ChatMessage[] | null; has_more_older?: boolean }>(
    res,
    'Не удалось загрузить сообщения'
  );
  return {
    messages: asArray<ChatMessage>(data.messages),
    has_more_older: Boolean(data.has_more_older),
  };
}

export async function sendStaffChatMessage(
  conversationId: string,
  body: string,
  attachmentIds: string[] = []
): Promise<ChatMessage> {
  const res = await fetch(`${API_BASE}/api/v1/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ body, attachment_ids: attachmentIds }),
    credentials: 'include',
  });
  return parseResponse(res, 'Не удалось отправить сообщение');
}

export async function uploadStaffChatAttachment(conversationId: string, file: File, filename?: string): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', file, filename || file.name || 'file');
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Client-ID': apiClient.clientId };
  const token = apiClient.getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/api/v1/chat/conversations/${conversationId}/attachments`, {
    method: 'POST',
    headers,
    body: form,
    credentials: 'include',
  });
  return parseResponse(res, 'Не удалось загрузить файл');
}

/** Скачать файл из staff-чата (требует Authorization). Возвращает Blob и оригинальное имя. */
export async function downloadStaffChatAttachment(attachmentId: string, filename: string): Promise<void> {
  const headers: Record<string, string> = { Accept: '*/*', 'X-Client-ID': apiClient.clientId };
  const token = apiClient.getSessionToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/api/v1/chat/attachments/${attachmentId}`, {
    headers,
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error('Не удалось скачать файл');
  }
  const blob = await res.blob();
  triggerBrowserDownload(blob, filename);
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  if (typeof window === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function assignChat(conversationId: string, assigneeIds: string[]): Promise<ChatConversation> {
  const res = await fetch(`${API_BASE}/api/v1/chat/conversations/${conversationId}/assign`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ assignee_ids: assigneeIds }),
    credentials: 'include',
  });
  return parseResponse(res, 'Не удалось назначить чат');
}

export async function setChatStatus(conversationId: string, status: 'open' | 'closed'): Promise<ChatConversation> {
  const action = status === 'closed' ? 'close' : 'reopen';
  const res = await fetch(`${API_BASE}/api/v1/chat/conversations/${conversationId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
  });
  return parseResponse(res, 'Не удалось обновить статус');
}

export async function markChatRead(conversationId: string): Promise<void> {
  await fetch(`${API_BASE}/api/v1/chat/conversations/${conversationId}/read`, {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
  });
}

export async function createInternalChat(data: {
  title: string;
  participant_user_ids: string[];
  message: string;
}): Promise<ChatConversation> {
  const res = await fetch(`${API_BASE}/api/v1/chat/internal-conversations`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
    credentials: 'include',
  });
  return parseResponse(res, 'Не удалось создать внутренний чат');
}

export async function listChatTechnicians(): Promise<User[]> {
  const res = await fetch(`${API_BASE}/api/v1/chat/technicians`, {
    headers: authHeaders(),
    credentials: 'include',
  });
  const data = await parseResponse<{ users: User[] }>(res, 'Не удалось загрузить техников');
  return data.users ?? [];
}

export async function startPublicChat(data: {
  name: string;
  phone: string;
  message: string;
  policy_consent: boolean;
  data_consent: boolean;
}): Promise<PublicChatStartResponse> {
  const res = await fetch(`${API_BASE}/api/v1/public-chat/conversations`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return parseResponse(res, 'Не удалось создать чат');
}

export async function listPublicChatMessages(
  conversationId: string,
  visitorToken: string,
  params: { limit?: number; beforeMessageId?: string } = {}
): Promise<{ messages: ChatMessage[]; has_more_older: boolean }> {
  const sp = new URLSearchParams({ visitor_token: visitorToken });
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.beforeMessageId) sp.set('before_message_id', params.beforeMessageId);
  const res = await fetch(`${API_BASE}/api/v1/public-chat/conversations/${conversationId}/messages?${sp.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  const data = await parseResponse<{ messages: ChatMessage[] | null; has_more_older?: boolean }>(
    res,
    'Не удалось загрузить сообщения'
  );
  return {
    messages: asArray<ChatMessage>(data.messages),
    has_more_older: Boolean(data.has_more_older),
  };
}

export async function sendPublicChatMessage(
  conversationId: string,
  visitorToken: string,
  body: string,
  attachmentIds: string[] = []
): Promise<ChatMessage> {
  const res = await fetch(`${API_BASE}/api/v1/public-chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ visitor_token: visitorToken, body, attachment_ids: attachmentIds }),
  });
  return parseResponse(res, 'Не удалось отправить сообщение');
}

export type PublicChatActivityType =
  | 'page_view'
  | 'widget_opened'
  | 'site_hidden'
  | 'site_visible'
  | 'site_left';

export async function sendPublicChatActivity(
  conversationId: string,
  visitorToken: string,
  activity: {
    type: PublicChatActivityType;
    url?: string;
    title?: string;
    referrer?: string;
    occurred_at?: string;
  }
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/v1/public-chat/conversations/${conversationId}/activity`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...activity, visitor_token: visitorToken }),
    keepalive: activity.type === 'site_hidden' || activity.type === 'site_left',
  });
  await parseResponse(res, 'Не удалось отправить действие посетителя');
}

export async function uploadPublicChatAttachment(
  conversationId: string,
  visitorToken: string,
  file: File,
  filename?: string
): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', file, filename || file.name || 'file');
  form.append('visitor_token', visitorToken);
  const sp = new URLSearchParams({ visitor_token: visitorToken });
  const res = await fetch(`${API_BASE}/api/v1/public-chat/conversations/${conversationId}/attachments?${sp.toString()}`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    body: form,
  });
  return parseResponse(res, 'Не удалось загрузить файл');
}

export function publicChatAttachmentURL(attachmentId: string, visitorToken: string): string {
  const sp = new URLSearchParams({ visitor_token: visitorToken });
  return `${API_BASE}/api/v1/public-chat/attachments/${attachmentId}?${sp.toString()}`;
}

export function publicChatEventsURL(conversationId: string, visitorToken: string): string {
  const sp = new URLSearchParams({ visitor_token: visitorToken });
  return `${API_BASE}/api/v1/public-chat/conversations/${conversationId}/events?${sp.toString()}`;
}

/** WebSocket для виджета посетителя (параллельно REST с токеном в query). */
export function publicChatWebSocketURL(conversationId: string, visitorToken: string): string {
  const sp = new URLSearchParams({ visitor_token: visitorToken });
  const path = `/api/v1/public-chat/conversations/${conversationId}/ws?${sp.toString()}`;
  const baseStr =
    API_BASE ||
    (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : 'http://localhost');
  const base = new URL(baseStr);
  const wsProto = base.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProto}//${base.host}${path}`;
}

/** SSE для консоли оператора (резерв; основной канал — WebSocket). */
export function staffChatEventsURL(): string | null {
  const token = apiClient.getSessionToken();
  if (!token) return null;
  const sp = new URLSearchParams({ access_token: token });
  return `${API_BASE}/api/v1/chat/events?${sp.toString()}`;
}

/** Основной WebSocket-канал реального времени для панели оператора. */
export function staffChatWebSocketURL(): string | null {
  const token = apiClient.getSessionToken();
  if (!token) return null;
  const sp = new URLSearchParams({ access_token: token });
  const path = `/api/v1/chat/ws?${sp.toString()}`;
  const baseStr =
    API_BASE ||
    (typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : 'http://localhost');
  const base = new URL(baseStr);
  const wsProto = base.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProto}//${base.host}${path}`;
}

export function formatChatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export async function approveDesktopPairing(pairingId: string, code: string, scheme: string): Promise<{ deep_link: string }> {
  const sp = new URLSearchParams({ code, scheme });
  const res = await fetch(`${API_BASE}/api/v1/desktop/pairing/${pairingId}/approve?${sp.toString()}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ callback_scheme: scheme }),
    credentials: 'include',
  });
  return parseResponse(res, 'Не удалось привязать приложение');
}
